/**
 * SeasonRecap - Phase 1.5 §5.2
 * Routes: /league/:id/recap
 * Renders only when League.status == "SEASON_COMPLETE". Otherwise <Navigate /> back home.
 */
import { useEffect, useState, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import { Crown, TrendingUp, Award, Zap, Activity, Users, Share2, Download } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";
import { authHeaders } from "../contexts/AuthContext";
import SeasonShell from "../components/season/SeasonShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SuperlativeCard({ icon: Icon, label, value, sublabel, isDark, testid }) {
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  return (
    <div
      className={`rounded-2xl ${cardBg} border ${cardBorder} p-5 hover:border-[#FF6B00]/60 transition-all`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-[#FF6B00]" />
        <span className={`text-[10px] uppercase tracking-[0.25em] ${muted} font-display font-bold`}>{label}</span>
      </div>
      <div className={`font-display font-black text-xl sm:text-2xl ${fg} mb-1`}>{value || "-"}</div>
      {sublabel && <div className={`text-xs ${muted}`}>{sublabel}</div>}
    </div>
  );
}

export default function SeasonRecap() {
  const { id: leagueId } = useParams();
  const { isDark } = useTheme();

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const heroRef = useRef(null);

  useEffect(() => {
    setLoadError(null);
    axios.get(`${API}/season/${leagueId}/recap`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch((e) => setLoadError(e?.response?.data?.detail || e?.message || "Failed to load recap"));
  }, [leagueId]);

  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  if (!data) {
    if (loadError) {
      return (
        <SeasonShell>
          <div className="py-20 text-center" data-testid="recap-load-error">
            <p className="font-display font-black uppercase text-xl text-[#FF4444] mb-2">Could not load recap</p>
            <p className={`font-body text-sm ${muted} mb-4`}>{loadError}</p>
          </div>
        </SeasonShell>
      );
    }
    return <SeasonShell><div className="py-20 text-center"><span className="font-display font-black uppercase text-2xl text-[#FF6B00]">Loading recap…</span></div></SeasonShell>;
  }

  // Phase 1.5 §5.2 / [v1.1, P5] - redirect if season not complete
  if (data.status !== "COMPLETE") {
    toast?.info?.("Season recap not available yet - championship hasn't been played.");
    return <Navigate to={`/league/${leagueId}/home`} replace />;
  }

  const r = data.recap_payload || {};
  const champion = r.champion || {};
  const runnerUp = r.runner_up || {};
  const finalStandings = r.final_standings || [];

  // Share handlers - exact DraftRecap pattern, content swapped. [Phase 1.5 sign-off follow-up]
  // Captures the hero+superlatives+standings block at heroRef via html2canvas, 2x scale.
  // Native share when supported (mobile + some desktop browsers); falls back to PNG download.
  const handleDownloadPNG = async () => {
    if (!heroRef.current) return;
    try {
      const canvas = await html2canvas(heroRef.current, {
        backgroundColor: isDark ? "#111111" : "#F0F0F0",
        scale: 2,
      });
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `pgf-season-recap-${(champion.team_name || "champion").replace(/\s+/g, "-")}.png`;
      a.click();
    } catch (e) {
      console.error("PNG export failed", e);
    }
  };

  const handleNativeShare = async () => {
    if (!heroRef.current) return;
    try {
      const canvas = await html2canvas(heroRef.current, {
        backgroundColor: isDark ? "#111111" : "#F0F0F0",
        scale: 2,
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      const fileName = `pgf-season-recap-${(champion.team_name || "champion").replace(/\s+/g, "-")}.png`;
      const file = new File([blob], fileName, { type: "image/png" });
      const shareData = {
        title: `${champion.team_name || "Champion"} - ${data.season_year || ""} PGF Champion`,
        text: `${champion.team_name || "My league's champion"} took the ${data.league_name || "PGF"} crown in ${data.season_year || "the season"}. Full recap inside.`,
        files: [file],
      };
      if (navigator.canShare && navigator.canShare(shareData) && navigator.share) {
        await navigator.share(shareData);
        return;
      }
      await handleDownloadPNG();
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error("Share failed; falling back to download", e);
        await handleDownloadPNG();
      }
    }
  };

  return (
    <SeasonShell>
      <div ref={heroRef}>
      {/* HERO */}
      <div className="relative mb-10" data-testid="recap-hero">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF6B00] font-display font-bold mb-2">
          {data.league_name} · Season {data.season_year}
        </div>
        <div className="flex items-start gap-4">
          <Crown size={56} className="text-[#FFD700] flex-shrink-0" />
          <div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-none mb-2">
              {champion.team_name || "Champion"}
            </h1>
            <p className={`text-base ${muted}`}>
              {data.season_year} Champion · defeated <span className="font-bold text-[#FF6B00]">{runnerUp.team_name || "-"}</span>
            </p>
          </div>
        </div>
      </div>

      {/* SUPERLATIVES GRID */}
      <h2 className="font-display font-black uppercase tracking-wide text-sm text-[#FF6B00] mb-3">Season Superlatives</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="recap-superlatives">
        <SuperlativeCard
          icon={Zap} label="Highest single week"
          value={r.highest_single_week ? `${r.highest_single_week.points.toFixed(1)} pts` : "-"}
          sublabel={r.highest_single_week ? `${r.highest_single_week.team_name} · Week ${r.highest_single_week.week}` : null}
          isDark={isDark} testid="recap-highest-week"
        />
        <SuperlativeCard
          icon={TrendingUp} label="Most points all season"
          value={r.most_points_season ? `${r.most_points_season.total.toFixed(1)} pts` : "-"}
          sublabel={r.most_points_season?.team_name}
          isDark={isDark} testid="recap-most-points"
        />
        <SuperlativeCard
          icon={Award} label="Best record"
          value={r.best_record ? `${r.best_record.wins}-${r.best_record.losses}-${r.best_record.ties}` : "-"}
          sublabel={r.best_record?.team_name}
          isDark={isDark} testid="recap-best-record"
        />
        <SuperlativeCard
          icon={Activity} label="Biggest upset"
          value={r.biggest_upset ? `+${r.biggest_upset.margin.toFixed(1)} pts` : "-"}
          sublabel={r.biggest_upset ? `${r.biggest_upset.winner_team_name} · Week ${r.biggest_upset.week}` : "No upsets logged"}
          isDark={isDark} testid="recap-biggest-upset"
        />
        <SuperlativeCard
          icon={Users} label="Best trade"
          value={r.best_trade ? `Move score: ${r.best_trade.movement_score}` : "-"}
          sublabel={r.best_trade ? `${r.best_trade.proposing_team_name} ↔ ${r.best_trade.receiving_team_name}` : "No trades completed"}
          isDark={isDark} testid="recap-best-trade"
        />
        <SuperlativeCard
          icon={Activity} label="Most active manager"
          value={r.most_active_manager?.team_name || "-"}
          sublabel={r.most_active_manager
            ? `Score ${r.most_active_manager.score} · ${r.most_active_manager.breakdown?.lineup_changes || 0} lineups, ${r.most_active_manager.breakdown?.waiver_claims || 0} waivers, ${r.most_active_manager.breakdown?.trade_proposals || 0} trades`
            : null}
          isDark={isDark} testid="recap-most-active"
        />
      </div>

      {/* FINAL STANDINGS */}
      <h2 className="font-display font-black uppercase tracking-wide text-sm text-[#FF6B00] mb-3">Final Standings</h2>
      <div className={`rounded-2xl ${cardBg} border ${cardBorder} overflow-hidden mb-10`} data-testid="recap-final-standings">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-[10px] uppercase tracking-widest ${muted} ${isDark ? "bg-[#252525]" : "bg-[#F5F5F5]"}`}>
                <th className="px-3 py-3 text-left">#</th>
                <th className="px-3 py-3 text-left">Team</th>
                <th className="px-3 py-3 text-right">W</th>
                <th className="px-3 py-3 text-right">L</th>
                <th className="px-3 py-3 text-right hidden sm:table-cell">T</th>
                <th className="px-3 py-3 text-right">PF</th>
              </tr>
            </thead>
            <tbody>
              {finalStandings.map((row) => (
                <tr key={row.team_id} data-testid={`recap-standing-${row.rank}`}>
                  <td className={`px-3 py-2.5 font-display font-black ${row.rank === 1 ? "text-[#FFD700]" : muted}`}>
                    {row.rank === 1 ? <Crown size={14} className="inline -mt-1 text-[#FFD700]" /> : row.rank}
                  </td>
                  <td className="px-3 py-2.5">
                    <Link to={`/league/${leagueId}/team/${row.team_id}`}
                          className={`font-display font-bold uppercase text-xs hover:text-[#FF6B00] ${row.rank === 1 ? "text-[#FFD700]" : ""}`}>
                      {row.team_name}
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 text-right">{row.wins}</td>
                  <td className="px-3 py-2.5 text-right">{row.losses}</td>
                  <td className="px-3 py-2.5 text-right hidden sm:table-cell">{row.ties}</td>
                  <td className="px-3 py-2.5 text-right font-display font-black">{row.points_for.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      </div>
      {/* END heroRef capture region */}

      {/* CTA BAR */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="recap-cta-bar">
        <button
          onClick={handleNativeShare}
          className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-black uppercase tracking-wide text-sm transition-all"
          data-testid="recap-share"
        >
          <Share2 size={14} /> Share Recap
        </button>
        <button
          onClick={handleDownloadPNG}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl ${cardBg} border ${cardBorder} hover:border-[#FF6B00]/60 font-display font-black uppercase tracking-wide text-sm transition-all`}
          data-testid="recap-download"
        >
          <Download size={14} /> Download PNG
        </button>
        <button
          disabled
          title="Multi-season support arrives in Phase 2"
          className={`flex items-center gap-2 px-5 py-3 rounded-xl ${cardBg} border ${cardBorder} ${muted} font-display font-black uppercase tracking-wide text-sm transition-all opacity-60 cursor-not-allowed`}
          data-testid="recap-next-season"
        >
          Start Next Season →
        </button>
      </div>
    </SeasonShell>
  );
}
